import { useState } from "react";
import { useAllProviderEvaluations, useUpdateProviderEvaluation, calculateAverageScore, type ShiftaskProviderEvaluation } from "@/hooks/useShiftaskProviderEvaluation";
import { useClientProfiles, getClientDisplayName } from "@/hooks/useClientProfiles";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Edit, Share2, Download, User, Clock, CheckCircle, AlertTriangle, Users, Activity, Laptop, DollarSign, Star, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { toast } from "sonner";
import { generateProviderEvaluationPDF } from "@/utils/generateProviderEvaluationPDF";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";

const metrics = [
  { key: 'response_time', labelEl: 'Χρόνος Απόκρισης', icon: Clock },
  { key: 'incident_handling', labelEl: 'Διαχείριση Συμβάντων', icon: AlertTriangle },
  { key: 'guard_quality', labelEl: 'Ποιότητα Φρουρών', icon: Shield },
  { key: 'professionalism', labelEl: 'Επαγγελματισμός', icon: Users },
  { key: 'communication', labelEl: 'Επικοινωνία', icon: Activity },
  { key: 'technology_satisfaction', labelEl: 'Ικανοποίηση από Τεχνολογία', icon: Laptop },
  { key: 'reporting_quality', labelEl: 'Ποιότητα Αναφορών', icon: TrendingUp },
  { key: 'innovation', labelEl: 'Καινοτομία', icon: Star },
  { key: 'value_for_money', labelEl: 'Αξία για Χρήματα', icon: DollarSign },
  { key: 'contract_flexibility', labelEl: 'Ευελιξία Συμβολαίου', icon: Shield },
  { key: 'account_management', labelEl: 'Διαχείριση Λογαριασμού', icon: Users },
];

export const EvaluationMitigations = () => {
  const { data: evaluations = [] } = useAllProviderEvaluations();
  const updateMutation = useUpdateProviderEvaluation();
  const [selectedEvaluation, setSelectedEvaluation] = useState<ShiftaskProviderEvaluation | null>(null);
  const [mitigations, setMitigations] = useState<Record<string, string>>({});
  const [isOpen, setIsOpen] = useState(false);
  const { customization } = useAdminCustomization();
  const appName = customization?.app_name || 'Provider';
  
  // Get unique clients and fetch their profiles
  const clientIds = Array.from(new Set(evaluations.map(e => e.client_id)));
  const { data: clientProfiles = {} } = useClientProfiles(clientIds);

  const handleOpenDialog = (evaluation: ShiftaskProviderEvaluation) => {
    setSelectedEvaluation(evaluation);
    setMitigations(evaluation.admin_mitigations || {});
    setIsOpen(true);
  };

  const handleSaveMitigations = async () => {
    if (!selectedEvaluation) return;

    try {
      await updateMutation.mutateAsync({
        id: selectedEvaluation.id,
        updates: {
          admin_mitigations: mitigations,
        }
      });
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving mitigations:', error);
    }
  };

  const handleShareWithClient = async (evaluation: ShiftaskProviderEvaluation) => {
    if (!evaluation.admin_mitigations || Object.keys(evaluation.admin_mitigations).length === 0) {
      toast.error('Προσθέστε πρώτα mitigations πριν την κοινοποίηση');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: evaluation.id,
        updates: {
          mitigation_shared_at: new Date().toISOString(),
        }
      });
      toast.success('Τα mitigations κοινοποιήθηκαν στον πελάτη');
    } catch (error) {
      console.error('Error sharing mitigations:', error);
    }
  };

  const handleDownloadPDF = async (evaluation: ShiftaskProviderEvaluation) => {
    if (!evaluation.admin_mitigations) {
      toast.error('Δεν υπάρχουν mitigations για λήψη');
      return;
    }
    
    try {
      toast.info('Δημιουργία PDF με mitigations...');
      const clientName = getClientDisplayName(clientProfiles[evaluation.client_id]);
      const appName = customization?.app_name || 'OVIT';
      await generateProviderEvaluationPDF(evaluation, clientName, appName, true);
      toast.success('PDF λήφθηκε επιτυχώς');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Σφάλμα κατά τη δημιουργία του PDF');
    }
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 4) return "default";
    if (score >= 3) return "secondary";
    return "destructive";
  };

  const getScoreColor = (score: number) => {
    if (score >= 4) return "text-emerald-600 dark:text-emerald-400";
    if (score >= 3) return "text-blue-600 dark:text-blue-400";
    return "text-red-600 dark:text-red-400";
  };

  // Group evaluations by client and period
  const groupedEvaluations = evaluations.reduce((acc, evaluation) => {
    const key = `${evaluation.client_id}-${evaluation.evaluation_period}-${evaluation.evaluation_year}`;
    if (!acc[key] || new Date(evaluation.submitted_at) > new Date(acc[key].submitted_at)) {
      acc[key] = evaluation;
    }
      return acc;
    }, {} as Record<string, ShiftaskProviderEvaluation>);

  const latestEvaluations = Object.values(groupedEvaluations).sort(
    (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="bg-gradient-to-br from-primary to-primary/80 p-3 rounded-lg">
              <Shield className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-3xl">Αξιολογήσεις {appName} - Σχέδια Δράσης</CardTitle>
          <CardDescription>
            Διαχείριση και κοινοποίηση σχεδίων δράσης για αξιολογήσεις πελατών
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Evaluation Cards Grid */}
      <div className="grid gap-6">
        {latestEvaluations.map((evaluation) => {
          const avgScore = calculateAverageScore(evaluation);
          const hasMitigations = evaluation.admin_mitigations && Object.keys(evaluation.admin_mitigations).length > 0;
          const isShared = !!evaluation.mitigation_shared_at;
          
          return (
            <Card key={evaluation.id} className="hover:shadow-lg transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  {/* Left Side Info */}
                  <div className="space-y-3 flex-1">
                    <div>
                      <h3 className="text-2xl font-bold">
                        {evaluation.evaluation_period} {evaluation.evaluation_year}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="default" className="gap-1">
                          🟢 Latest - Final
                        </Badge>
                        {hasMitigations && (
                          <Badge variant="secondary" className="gap-1">
                            🔵 Mitigations Added
                          </Badge>
                        )}
                        {isShared && (
                          <Badge variant="outline" className="gap-1">
                            🟣 Shared with Client
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Client:</span>
                        <span className="text-muted-foreground font-semibold">
                          {getClientDisplayName(clientProfiles[evaluation.client_id])}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Submitted:</span>
                        <span className="text-muted-foreground">
                          {format(new Date(evaluation.submitted_at), 'PPp', { locale: el })}
                        </span>
                      </div>
                      {isShared && (
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <span className="font-medium">Shared:</span>
                          <span className="text-muted-foreground">
                            {format(new Date(evaluation.mitigation_shared_at!), 'PPp', { locale: el })}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-primary fill-primary" />
                      <span className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>
                        {avgScore.toFixed(1)}/5
                      </span>
                      <span className="text-sm text-muted-foreground">Average Score</span>
                    </div>
                  </div>

                  {/* Right Side Actions */}
                  <div className="flex flex-col gap-2">
                    <Dialog open={isOpen && selectedEvaluation?.id === evaluation.id} onOpenChange={(open) => {
                      setIsOpen(open);
                      if (!open) setSelectedEvaluation(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDialog(evaluation)}
                          className="gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          {hasMitigations ? 'Edit Mitigations' : 'Add Mitigations'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            Mitigations - {evaluation.evaluation_period} {evaluation.evaluation_year}
                          </DialogTitle>
                          <DialogDescription>
                            Client: {getClientDisplayName(clientProfiles[evaluation.client_id])}
                          </DialogDescription>
                        </DialogHeader>

                        <Tabs defaultValue="scores" className="w-full">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="scores">Evaluation Scores</TabsTrigger>
                            <TabsTrigger value="mitigations">Mitigations</TabsTrigger>
                          </TabsList>

                          <TabsContent value="scores" className="space-y-4 mt-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              {metrics.map((metric) => {
                                const Icon = metric.icon;
                                const score = evaluation[`${metric.key}_score` as keyof ShiftaskProviderEvaluation] as number;
                                const comments = evaluation[`${metric.key}_comments` as keyof ShiftaskProviderEvaluation] as string;
                                
                                return (
                                  <Card key={metric.key}>
                                    <CardHeader className="pb-3">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <Icon className="h-4 w-4" />
                                          <CardTitle className="text-sm">{metric.labelEl}</CardTitle>
                                        </div>
                                        <Badge 
                                          variant={getScoreBadgeVariant(score)}
                                          className={score >= 4 ? "bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300" : ""}
                                        >
                                          {score}/5
                                        </Badge>
                                      </div>
                                      {comments && (
                                        <CardDescription className="text-xs mt-2 p-2 bg-muted rounded">
                                          {comments}
                                        </CardDescription>
                                      )}
                                    </CardHeader>
                                  </Card>
                                );
                              })}
                            </div>
                          </TabsContent>

                          <TabsContent value="mitigations" className="space-y-4 mt-4">
                            {metrics.map((metric) => (
                              <div key={metric.key} className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <metric.icon className="h-4 w-4" />
                                  {metric.labelEl}
                                  <Badge variant={getScoreBadgeVariant(
                                    evaluation[`${metric.key}_score` as keyof ShiftaskProviderEvaluation] as number
                                  )}>
                                    {String(evaluation[`${metric.key}_score` as keyof ShiftaskProviderEvaluation])}/5
                                  </Badge>
                                </Label>
                                <Textarea
                                  placeholder={`Add mitigation strategy for ${metric.labelEl.toLowerCase()}...`}
                                  value={mitigations[metric.key] || ''}
                                  onChange={(e) => setMitigations({
                                    ...mitigations,
                                    [metric.key]: e.target.value
                                  })}
                                  rows={3}
                                  className="resize-none"
                                />
                              </div>
                            ))}

                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Star className="h-4 w-4" />
                                Overall Satisfaction
                                <Badge>{evaluation.overall_satisfaction}/10</Badge>
                              </Label>
                              <Textarea
                                placeholder="Add overall satisfaction mitigation..."
                                value={mitigations['overall_satisfaction'] || ''}
                                onChange={(e) => setMitigations({
                                  ...mitigations,
                                  overall_satisfaction: e.target.value
                                })}
                                rows={4}
                                className="resize-none"
                              />
                            </div>
                          </TabsContent>
                        </Tabs>

                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSaveMitigations}
                            disabled={updateMutation.isPending}
                          >
                            {updateMutation.isPending ? 'Saving...' : 'Save Mitigations'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {hasMitigations && !isShared && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleShareWithClient(evaluation)}
                        disabled={updateMutation.isPending}
                        className="gap-2"
                      >
                        <Share2 className="h-4 w-4" />
                        Share with Client
                      </Button>
                    )}

                    {hasMitigations && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadPDF(evaluation)}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {latestEvaluations.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Δεν υπάρχουν αξιολογήσεις</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
